// The sync engine (sync.md §4–§7): local changes apply at once and queue in the outbox; one tab, the
// leader, pushes them in batches and pulls from the stored cursor. Every tab writes to the same
// IndexedDB, and a BroadcastChannel tells the others what changed, so the tabs of one browser act as
// one engine. The leader is chosen with the Web Locks API (whichever tab holds the lock); when it
// closes, another tab takes the lock and carries on.
//
// Push: soon after a change (1.5 s debounce), on start, on focus, when the browser comes back online,
// and every minute while the page is visible. Pull: after every push and on the same triggers.
// Failures back off exponentially with full jitter (1 s … 5 min, honouring Retry-After).
import * as ops from "./ops";
import type { Store } from "./store";
import type { Collection, OutboxRow, PullPage, PushResult } from "./types";

export type Network = "idle" | "syncing" | "offline" | "paused" | "signed-out" | "error" | "local";
export type SyncStatus = { pending: number; network: Network; lastSyncedAt: number | null; message: string | null };

type Message = { type: "changed"; collections: Collection[] } | { type: "kick" } | { type: "status"; network: Network; lastSyncedAt: number | null; message: string | null };

export type EngineOptions = {
  store: Store;
  /** Names the lock and the channel: one engine per account per browser. */
  account: string;
  /** The API base, or null on the prototype data: then everything stays in this browser. */
  baseUrl: string | null;
  fetch?: typeof fetch;
  /** For tests: no timers, no lock, no channel. */
  manual?: boolean;
};

const MAX_BACKOFF = 300_000;
const DEBOUNCE = 1_500;
const INTERVAL = 60_000;

/** Thrown for a response the loop handles by status. */
class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly retryAfter: number | null,
  ) {
    super(`HTTP ${status}`);
  }
}

export class SyncEngine {
  readonly store: Store;
  private readonly opts: EngineOptions;
  private readonly doFetch: typeof fetch;
  private channel: BroadcastChannel | null = null;
  private leader = false;
  private releaseLock: (() => void) | null = null;
  private running: Promise<void> | null = null;
  private again = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private backoff = 0;
  private batchLimit = ops.PUSH_LIMIT;
  private stopped = false;
  private status: SyncStatus;
  private listeners = new Set<(s: SyncStatus) => void>();
  private changeListeners = new Set<(c: Set<Collection>) => void>();
  private cleanup: (() => void)[] = [];

  constructor(opts: EngineOptions) {
    this.opts = opts;
    this.store = opts.store;
    this.doFetch = opts.fetch ?? ((i, init) => fetch(i, init));
    this.status = { pending: 0, network: opts.baseUrl ? "idle" : "local", lastSyncedAt: null, message: null };
  }

  get account() {
    return this.opts.account;
  }

  get baseUrl() {
    return this.opts.baseUrl;
  }

  /** Joins the tabs, takes the lead if no other tab has it, and syncs. */
  start() {
    if (this.opts.manual) return;
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(`bs-sync-${this.opts.account}`);
      this.channel.onmessage = (e: MessageEvent<Message>) => this.onMessage(e.data);
    }
    void this.refreshPending();
    if (!this.opts.baseUrl) return;
    const lead = () => {
      this.leader = true;
      void ops.resetInFlight(this.store).then(() => this.syncNow());
      const wake = () => this.syncNow();
      const onVisible = () => document.visibilityState === "visible" && wake();
      const offline = () => this.setNetwork("offline", null);
      window.addEventListener("focus", wake);
      window.addEventListener("online", wake);
      window.addEventListener("offline", offline);
      document.addEventListener("visibilitychange", onVisible);
      this.interval = setInterval(() => document.visibilityState === "visible" && wake(), INTERVAL);
      this.cleanup.push(() => {
        window.removeEventListener("focus", wake);
        window.removeEventListener("online", wake);
        window.removeEventListener("offline", offline);
        document.removeEventListener("visibilitychange", onVisible);
      });
    };
    if (typeof navigator !== "undefined" && navigator.locks) {
      void navigator.locks.request(`bs-sync-${this.opts.account}`, () => {
        if (this.stopped) return;
        lead();
        return new Promise<void>((resolve) => (this.releaseLock = resolve));
      });
    } else lead();
  }

  stop() {
    this.stopped = true;
    this.releaseLock?.();
    this.channel?.close();
    if (this.timer) clearTimeout(this.timer);
    if (this.interval) clearInterval(this.interval);
    this.cleanup.forEach((f) => f());
    this.listeners.clear();
    this.changeListeners.clear();
  }

  // ---- Local changes ----

  async mutate(changes: ops.Change[]) {
    const out = await ops.mutate(this.store, changes);
    const collections = [...new Set(changes.map((c) => c.collection))];
    this.emitChange(new Set(collections));
    this.post({ type: "changed", collections });
    await this.refreshPending();
    if (this.leader) this.schedule(DEBOUNCE);
    else this.post({ type: "kick" });
    return out;
  }

  // ---- Status and change notifications ----

  getStatus = () => this.status;

  onStatus(fn: (s: SyncStatus) => void) {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  onChange(fn: (c: Set<Collection>) => void) {
    this.changeListeners.add(fn);
    return () => void this.changeListeners.delete(fn);
  }

  private emitChange(c: Set<Collection>) {
    this.changeListeners.forEach((f) => f(c));
  }

  private setStatus(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((f) => f(this.status));
  }

  private setNetwork(network: Network, message: string | null, lastSyncedAt = this.status.lastSyncedAt) {
    this.setStatus({ network, message, lastSyncedAt });
    this.post({ type: "status", network, lastSyncedAt, message });
  }

  private async refreshPending() {
    try {
      this.setStatus({ pending: await ops.pendingCount(this.store) });
    } catch {
      // The store is closing (signing out).
    }
  }

  private post(m: Message) {
    try {
      this.channel?.postMessage(m);
    } catch {
      // Closed.
    }
  }

  private onMessage(m: Message) {
    if (m.type === "changed") {
      this.emitChange(new Set(m.collections));
      void this.refreshPending();
    } else if (m.type === "kick") {
      if (this.leader) this.schedule(DEBOUNCE);
    } else if (m.type === "status") {
      this.setStatus({ network: m.network, lastSyncedAt: m.lastSyncedAt, message: m.message });
      void this.refreshPending();
    }
  }

  // ---- The loop ----

  private schedule(ms: number) {
    if (this.stopped || this.opts.manual) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.syncNow();
    }, ms);
  }

  /** Push everything queued, then pull everything new. Safe to call at any time; runs one at a time. */
  syncNow(): Promise<void> {
    if (!this.opts.baseUrl || this.stopped) return Promise.resolve();
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        await this.cycle();
      } while (this.again && !this.stopped);
    })().finally(() => (this.running = null));
    return this.running;
  }

  private async cycle() {
    this.setNetwork("syncing", null);
    try {
      const pushed = await this.pushAll();
      await this.pullAll();
      this.backoff = 0;
      this.setNetwork("idle", null, Date.now());
      if (pushed === "limit") this.setNetwork("error", "You’ve reached the limit for one kind of note. Edits and deletions still sync.");
    } catch (e) {
      this.onFailure(e);
    } finally {
      await this.refreshPending();
    }
  }

  private onFailure(e: unknown) {
    if (e instanceof HttpError) {
      if (e.status === 401) return this.setNetwork("signed-out", "Sign in again to sync. Your changes are kept on this device.");
      if (e.status === 503) {
        const detail = problemDetail(e.body);
        this.setNetwork("paused", detail ?? "Sync is paused for now. Your changes are kept on this device.");
        return this.retryLater(e.retryAfter, 60_000);
      }
      this.setNetwork("error", null);
      return this.retryLater(e.retryAfter);
    }
    // A network failure.
    this.setNetwork("offline", null);
    this.retryLater(null);
  }

  private retryLater(retryAfter: number | null, floor = 0) {
    this.backoff = Math.min(MAX_BACKOFF, this.backoff ? this.backoff * 2 : 1_000);
    const jitter = Math.random() * this.backoff;
    this.schedule(Math.max(retryAfter ?? 0, floor, jitter));
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await this.doFetch(`${this.opts.baseUrl}${path}`, {
      credentials: "include",
      ...init,
      headers: { accept: "application/json", ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers },
    });
    const text = await res.text();
    let body: unknown;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    if (!res.ok) {
      const ra = res.headers.get("retry-after");
      throw new HttpError(res.status, body, ra && /^\d+$/.test(ra) ? Number(ra) * 1000 : null);
    }
    return body as T;
  }

  /** Push the outbox in batches. 400s dead-letter the named mutations; 413 halves the batch. */
  private async pushAll(): Promise<"ok" | "limit"> {
    let result: "ok" | "limit" = "ok";
    for (let guard = 0; guard < 200; guard++) {
      const batch = await ops.takeBatch(this.store, this.batchLimit);
      if (batch.length === 0) break;
      try {
        await this.request<PushResult>("/v1/sync/push", { method: "POST", body: JSON.stringify({ mutations: batch.map(ops.toWire) }) });
        await ops.settle(this.store, batch);
        this.batchLimit = ops.PUSH_LIMIT;
      } catch (e) {
        if (e instanceof HttpError && e.status === 400) {
          const bad = badRows(e.body, batch);
          // A refusal that names nothing would loop forever: set the whole batch aside.
          const aside = bad.length ? bad : batch;
          await ops.deadLetter(this.store, aside, problemDetail(e.body) ?? "refused");
          await ops.release(
            this.store,
            batch.filter((r) => !aside.includes(r)),
          );
          continue;
        }
        if (e instanceof HttpError && e.status === 413) {
          if (batch.length === 1) await ops.deadLetter(this.store, batch, "too large");
          else {
            await ops.release(this.store, batch);
            this.batchLimit = Math.max(1, Math.floor(batch.length / 2));
          }
          continue;
        }
        if (e instanceof HttpError && e.status === 409) {
          // limit-reached: keep the creates queued; they go once the reader has made room.
          await ops.release(this.store, batch);
          result = "limit";
          break;
        }
        await ops.release(this.store, batch);
        throw e;
      }
    }
    return result;
  }

  private async pullAll() {
    const meta = await ops.readMeta(this.store);
    let cursor = meta.resyncing ? 0 : meta.cursor;
    let seen: Set<string> | undefined = meta.resyncing ? new Set() : undefined;
    for (let guard = 0; guard < 1000; guard++) {
      const page = await this.request<PullPage>(`/v1/sync/pull?cursor=${cursor}&limit=500`);
      if (page.resync) {
        cursor = 0;
        seen = new Set();
        await ops.patchMeta(this.store, { cursor: 0, resyncing: true });
        continue;
      }
      const touched = await ops.applyPage(this.store, page, seen);
      cursor = page.nextCursor;
      if (touched.size) {
        this.emitChange(touched);
        this.post({ type: "changed", collections: [...touched] });
      }
      if (!page.hasMore) break;
    }
    if (seen) await ops.finishResync(this.store, seen);
  }
}

function problemDetail(body: unknown): string | null {
  const d = (body as { detail?: unknown } | null)?.detail;
  return typeof d === "string" ? d : null;
}

/** The rows a 400 names: its errors' paths begin `mutations.N`. */
function badRows(body: unknown, batch: OutboxRow[]): OutboxRow[] {
  const errors = (body as { errors?: { path?: string }[] } | null)?.errors ?? [];
  const at = new Set(
    errors
      .map((e) => /^mutations\.(\d+)/.exec(e.path ?? "")?.[1])
      .filter((x): x is string => !!x)
      .map(Number),
  );
  return batch.filter((_, i) => at.has(i));
}
